import { useEffect, useRef, useState } from 'react';

/**
 * A date-time / time field that ALWAYS displays 24-HOUR (military) time — never
 * AM/PM — whatever the viewer's browser locale is (standing rule 3; GoLive parity,
 * user request 2026-09-24).
 *
 * Why this exists: a native `<input type="datetime-local">` / `<input type="time">`
 * renders its text in the BROWSER's locale (en-US → `hh:mm AM/PM`) and that cannot
 * be overridden — not by CSS, not by the `lang` attribute (same limitation
 * UsDateInput documents for `type="date"`). So the TEXT is ours — a plain text
 * input formatted / parsed as `MM/DD/YYYY HH:mm` (datetime) or `HH:mm` (time) —
 * while the PICKER is still the browser's: a visually-hidden native input is kept
 * alongside and opened with `showPicker()` from the clock/calendar button.
 *
 * The value crossing `onChange` is the SAME string the native input it replaces
 * would store — `yyyy-mm-ddTHH:mm` (datetime) or `HH:mm` (time) — so it is a
 * drop-in replacement.
 */

type Mode = 'datetime' | 'time';

const pad = (n: number) => String(n).padStart(2, '0');

/** ISO `yyyy-mm-ddTHH:mm` → display `MM/DD/YYYY HH:mm` ('' when absent). */
function dtToUs(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]} ${m[4]}:${m[5]}` : '';
}

/** display `MM/DD/YYYY HH:mm` (also `M/D/YY H:mm`) → ISO `yyyy-mm-ddTHH:mm`; ''
 *  when it isn't a real date-time. Seconds/AM-PM are tolerated on input. */
function usToDt(text: string | null | undefined): string {
  if (!text) return '';
  const m = /^\s*(\d{1,2})\s*[/\-.]\s*(\d{1,2})\s*[/\-.]\s*(\d{2}|\d{4})[\s,T]+(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)?\s*$/i.exec(text);
  if (!m) return '';
  const [, mRaw, dRaw, yRaw, hRaw, minRaw, ap] = m as unknown as [string, string, string, string, string, string, string | undefined];
  const mm = Number(mRaw), dd = Number(dRaw), min = Number(minRaw);
  let yy = Number(yRaw), h = Number(hRaw);
  if (yRaw.length === 2) yy += yy < 50 ? 2000 : 1900;
  if (ap) { const p = ap.toLowerCase(); if (p === 'pm' && h < 12) h += 12; if (p === 'am' && h === 12) h = 0; }
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || h > 23 || min > 59) return '';
  const d = new Date(yy, mm - 1, dd);
  if (d.getFullYear() !== yy || d.getMonth() !== mm - 1 || d.getDate() !== dd) return '';
  return `${yy}-${pad(mm)}-${pad(dd)}T${pad(h)}:${pad(min)}`;
}

/** `HH:mm` (also `H:mm`, `h:mm AM/PM`) → 24-hour `HH:mm`; '' when invalid. */
function normTime(text: string | null | undefined): string {
  if (!text) return '';
  const m = /^\s*(\d{1,2}):(\d{2})(?::\d{2})?\s*(am|pm)?\s*$/i.exec(text);
  if (!m) return '';
  let h = Number(m[1]); const min = Number(m[2]); const ap = m[3];
  if (ap) { const p = ap.toLowerCase(); if (p === 'pm' && h < 12) h += 12; if (p === 'am' && h === 12) h = 0; }
  if (h > 23 || min > 59) return '';
  return `${pad(h)}:${pad(min)}`;
}

export function UsDateTimeInput({ id, className, mode, value, readOnly, onChange }: {
  id?: string;
  className?: string;
  mode: Mode;
  /** ISO `yyyy-mm-ddTHH:mm` (datetime) or `HH:mm` (time), or '' / null when empty. */
  value: string | null | undefined;
  readOnly?: boolean;
  /** Receives the same native string (`yyyy-mm-ddTHH:mm` / `HH:mm`), or '' when cleared. */
  onChange: (v: string) => void;
}) {
  const toText = (v: string | null | undefined) => (mode === 'time' ? normTime(v) : dtToUs(v));
  const parse = (t: string) => (mode === 'time' ? normTime(t) : usToDt(t));

  const [text, setText] = useState(() => toText(value));
  const pickerRef = useRef<HTMLInputElement>(null);

  // Track upstream changes (record switch, Cancel, Save & New, TAT auto-fill) —
  // but never while the user is mid-edit with an as-yet-unparseable string.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setText(toText(value)); }, [value, mode]);

  /** Commit on blur: a valid value is normalised, anything else reverts. */
  const commit = () => {
    const raw = text.trim();
    if (raw === '') { setText(''); onChange(''); return; }
    const v = parse(raw);
    if (v) { setText(toText(v)); onChange(v); }
    else setText(toText(value));
  };

  return (
    <span className="usdate">
      <input
        id={id}
        className={className}
        type="text"
        inputMode="numeric"
        aria-label={mode === 'time' ? 'Time, 24-hour HH:mm' : 'Date and time, MM/DD/YYYY HH:mm'}
        value={text}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
      />
      {!readOnly && (
        <>
          <button
            type="button"
            className="usdate-btn"
            aria-label={mode === 'time' ? 'Open time picker' : 'Open date-time picker'}
            title={mode === 'time' ? 'Open time picker' : 'Open date-time picker'}
            onClick={() => {
              const el = pickerRef.current;
              if (!el) return;
              try { (el as HTMLInputElement & { showPicker?: () => void }).showPicker?.(); }
              catch { el.focus(); }
            }}
          >
            <i className={mode === 'time' ? 'ti ti-clock' : 'ti ti-calendar'} />
          </button>
          {/* The native picker — present so the calendar/clock still works, never
              shown (its AM/PM text is what we're hiding), kept out of the tab order. */}
          <input
            ref={pickerRef}
            className="usdate-native"
            type={mode === 'time' ? 'time' : 'datetime-local'}
            tabIndex={-1}
            aria-hidden="true"
            value={value ?? ''}
            onChange={(e) => { setText(toText(e.target.value)); onChange(e.target.value); }}
          />
        </>
      )}
    </span>
  );
}
